import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const PaymentDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [loading, setLoading] = useState(false);

  const { paymentData, planType } = location.state || {};

  useEffect(() => {
    if (!paymentData) {
      navigate('/pricing');
      return;
    }

    // Check payment status periodically
    const checkStatus = async () => {
      try {
        const response = await fetch(`${BACKEND_API_URL}/invoice/${paymentData.invoice_id}`);
        const data = await response.json();
        
        if (data.status === 'paid') {
          setPaymentStatus('paid');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    const statusInterval = setInterval(checkStatus, 30000); // Check every 30 seconds

    return () => {
      clearInterval(statusInterval);
    };
  }, [paymentData, navigate]);

  const getPlanDetails = () => {
    const plans = {
      core: { name: 'Core Plan', price: 97, color: '#10B981', emoji: '🟢' },
      growth: { name: 'Growth Plan', price: 497, color: '#3B82F6', emoji: '🔵' },
      enterprise: { name: 'Enterprise Plan', price: 997, color: '#8B5CF6', emoji: '🟣' },
      ultimate: { name: 'Ultimate Plan', price: 2997, color: '#F59E0B', emoji: '⭐' },
      ultamega: { name: 'Ulta Mega Plan', price: 4997, color: '#EF4444', emoji: '🔥' }
    };
    return plans[planType] || plans.core;
  };

  const planDetails = getPlanDetails();

  if (!paymentData) {
    return null;
  }

  if (paymentStatus === 'paid') {
    return (
      <Container>
        <SuccessCard>
          <SuccessIcon>✅</SuccessIcon>
          <SuccessTitle>Payment Confirmed!</SuccessTitle>
          <SuccessMessage>
            Your {planDetails.name} subscription has been activated. You now have access to all API features.
          </SuccessMessage>
          <SuccessDetails>
            <DetailItem>
              <DetailLabel>Invoice ID:</DetailLabel>
              <DetailValue>{paymentData.invoice_id}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Amount Paid:</DetailLabel>
              <DetailValue>{paymentData.amount_usdt} USDT (${paymentData.amount_usd})</DetailValue>
            </DetailItem>
          </SuccessDetails>
          <ActionButton onClick={() => navigate('/')}>
            Go to Dashboard
          </ActionButton>
        </SuccessCard>
      </Container>
    );
  }

  console.log('Checkout URL:', paymentData.checkout_url);

  return (
    <Container>
      <PaymentCard>
        <Header>
          <PlanEmoji>{planDetails.emoji}</PlanEmoji>
          <PlanTitle>{planDetails.name}</PlanTitle>
          <PlanPrice>${planDetails.price}/month</PlanPrice>
        </Header>

        <Content>
          <StatusSection>
            <StatusTitle>USDT Payment Required</StatusTitle>
          </StatusSection>

          <ActionSection>
            <PrimaryButton onClick={() => window.open(paymentData.checkout_url, '_blank')}>
              Open Payment Page
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/pricing')}>
              Back to Pricing
            </SecondaryButton>
          </ActionSection>
        </Content>
      </PaymentCard>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: #F9FAFB;
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PaymentCard = styled.div`
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  text-align: center;
`;

const PlanEmoji = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const PlanTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: 800;
`;

const Content = styled.div`
  padding: 2rem;
`;

const StatusSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const StatusTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PrimaryButton = styled.button`
  background: #000000;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #333333;
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #000000;
  border: 1px solid #D1D5DB;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #F9FAFB;
    border-color: #000000;
  }
`;

// Success Card Components
const SuccessCard = styled.div`
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
  padding: 3rem 2rem;
  text-align: center;
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const SuccessTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.p`
  color: #6B7280;
  font-size: 1.125rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const SuccessDetails = styled.div`
  background: #F9FAFB;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: left;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #374151;
`;

const DetailValue = styled.span`
  color: #6B7280;
  font-family: monospace;
`;

const ActionButton = styled.button`
  background: #10B981;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #059669;
    transform: translateY(-1px);
  }
`;

export default PaymentDetails; 